import { useQuery } from '@tanstack/react-query';
import { nexusFetch, nexusBearerFetch } from '../../nexus/nexusClient';
import { useAuth } from '../../auth/AuthProvider';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import {
  usageTopFixture,
  usagePluginStatusFixture,
  usageSummaryFixture,
} from './fixture';

/**
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-usage-explorer.md
 *
 * Drei Sub-Views, drei Endpoints:
 *  - GET /v1/usage/summary           (bestehend, Bearer-Auth)
 *  - GET /v1/admin/usage/top         (M2M-Admin)
 *  - GET /v1/admin/usage/plugin-status (M2M-Admin)
 */

function authHeader(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** GET /v1/usage/summary?subscription_id=&start=&end= */
export function useUsageSummary({ subscriptionId, start, end }) {
  const auth = useAuth();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-usage-summary', { subscriptionId, start, end }, fixturesMode ? 'fixture' : 'live'],
    enabled: !!subscriptionId && !!start && !!end,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return usageSummaryFixture({ subscriptionId });
      }
      const qs = new URLSearchParams();
      qs.set('subscription_id', subscriptionId);
      qs.set('start', start);
      qs.set('end', end);
      return nexusBearerFetch(`/v1/usage/summary?${qs.toString()}`, {
        headers: authHeader(auth.getAccessToken()),
      });
    },
    staleTime: 30_000,
  });
}

/** GET /v1/admin/usage/top?tenant_id=&resource_type=&start=&end=&limit= */
export function useUsageTop({ tenantId, resourceType, start, end, limit = 10 }) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-usage-top', { tenantId, resourceType, start, end, limit }, fixturesMode ? 'fixture' : 'live'],
    enabled: !!tenantId && !!resourceType,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return usageTopFixture({ resourceType });
      }
      const qs = new URLSearchParams();
      qs.set('tenant_id', tenantId);
      qs.set('resource_type', resourceType);
      if (start) qs.set('start', start);
      if (end) qs.set('end', end);
      qs.set('limit', String(limit));
      return nexusFetch(`/v1/admin/usage/top?${qs.toString()}`);
    },
    staleTime: 60_000,
  });
}

/** GET /v1/admin/usage/plugin-status */
export function useUsagePluginStatus() {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-usage-plugin-status', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return usagePluginStatusFixture();
      }
      const res = await nexusFetch('/v1/admin/usage/plugin-status');
      const list = res?.plugins ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { plugins: list, raw: res };
    },
    refetchInterval: 60_000,
  });
}
